"use client";

import { useRef } from "react";

import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import { ScrollTrigger } from "gsap/dist/ScrollTrigger";
import { PortableText } from "@portabletext/react";
import { customRichTextComponent } from "@/sanity/sanityPortableText";

import { CTAButton } from "@/shadcn/ui/button";
import Video from "@/components/global/Video";

import { PageHomeSchema } from "@/sanity/pages/PageHomeTemplate";

if (typeof window !== "undefined") {
  gsap.registerPlugin(ScrollTrigger, useGSAP);
}

export default function VideoSection({
  data,
  bitunix,
  phemex,
  pionex,
}: {
  data: PageHomeSchema;
  bitunix?: boolean;
  phemex?: boolean;
  pionex?: boolean;
}) {
  const containerRef = useRef<HTMLDivElement | any>(undefined);

  useGSAP(
    () => {
      const videoTL = gsap.timeline({
        defaults: { opacity: 0, ease: "power2.inOut", duration: 0.25 },
      });

      videoTL.from(containerRef.current.children, {
        y: "3rem",
        stagger: 0.2,
        delay: 0.5,
        scrollTrigger: {
          trigger: containerRef.current,
          end: "center center",
          scrub: 1,
        },
      });
    },
    { scope: containerRef }
  );

  return (
    <section className="w-full">
      <div className="container-lp mx-auto pt-[8rem] lg:pt-[15rem]">
        <div
          ref={containerRef}
          className="flex flex-col items-center justify-center w-full lg:w-[78%] mx-auto"
        >
          {data?.videoTitle && (
            <PortableText
              value={data?.videoTitle ?? []}
              components={customRichTextComponent({
                block: {
                  h2: ({ children }) => (
                    <h2 className="gradient-title text-[5rem] lg:text-[7rem] font-[600] tracking-[-0.49rem] leading-[110%] w-[60%] md:w-[35%] lg:w-[100%] text-center mb-[3rem]">
                      {children}
                    </h2>
                  ),
                },
                marks: {
                  orangeFlare: ({ children }) => (
                    <span
                      className={
                        bitunix
                          ? "bitunix-shadow"
                          : phemex
                            ? "phemex-shadow"
                            : pionex
                              ? "pionex-shadow"
                              : "orange-shadow"
                      }
                    >
                      {children}
                    </span>
                  ),
                },
              })}
            />
          )}

          <Video videoData={data?.video} />

          <div className="mt-[3rem]">
            {data?.videoCTA && <CTAButton data={data?.videoCTA} />}
          </div>
        </div>
      </div>
    </section>
  );
}
